import { useLocation, useNavigate } from "react-router-dom"
import { buildMeasurementPath, formatDate, formatOrdinal, getSampleIdFromSearch, PATH_INFO } from "../utils/helper"
import { useCallback, useContext, useEffect, useRef, useState } from "react"
import fileImg from '../../assets/images/icons/file-upload.svg'
import { Modal } from "../../components/Modal"
import errorImg from '../../assets/images/icons/errorOutline.svg'
import InfiniteScroll from "react-infinite-scroll-component"
import ReactLoading from "react-loading"
import { clientFetch } from "../../utils/clientFetch"
import { SamplesContext } from "../../context/samplesContext"
import { SizeCandidateInput } from "../../components/input/SizeCandidateField"
import i18n from "../../i18n"
import { useTranslation } from "react-i18next"

type SpecType = {
  created_at: string
  part_number: string
  sample_number: string
  kind: string
  repeat: number
  size: string
  color_no: string
  time: number
  id: string
}

type SpecMeta = {
  total: number
  page: number
  limit: number
  hasMore: boolean
  nextPage: number | null
}

type SpecApiResponse = {
  data: SpecType[]
  meta: SpecMeta
}

type ManualSpecItemInput = {
  id: string
  title: string
  value: string
}

type ManualSpecForm = {
  brand: string
  partNumber: string
  sampleNumber: string
  repeat: string
  itemName: string
  manufacturer: string
  patternNumber: string
  kind: '' | 'final' | 'sample'
  designedAt: string
  time: string
  colorNo: string
  size: string
  items: ManualSpecItemInput[]
}

type ManualSpecPayload = {
  brand: string
  part_number?: string | null
  sample_number?: string | null
  repeat: number
  item_name: string
  manufacturer?: string | null
  pattern_number?: string | null
  kind: 'final' | 'sample'
  designed_at: string
  time?: number | null
  color_no?: string | null
  size: string
  items: { title: string, value: number }[]
  onDuplicate?: 'overwrite' | 'cancel'
}

type BuildPayloadResult =
  | { ok: true, payload: ManualSpecPayload }
  | { ok: false, error: string }

type ApiErrorBody = {
  message?: string
  code?: string
  error_code?: string
  rowNumber?: number
  // valueは型が揺れるためunknownで受け、表示時に型チェックする
  value?: unknown
  duplicates?: {
    row: number
    part: string | null
    sample: string | null
    repeat: number
    size: string
    kind: string
  }[]
}

const SPEC_PAGE_LIMIT = 50
const DEFAULT_SPEC_META: SpecMeta = {
  total: 0,
  page: 1,
  limit: SPEC_PAGE_LIMIT,
  hasMore: false,
  nextPage: null
}
const SPEC_TABLE_SCROLL_ID = "spec-import-table-scroll"
const MANUAL_SPEC_ITEM_ID_START = 0
const MANUAL_SPEC_ITEM_ID_STEP = 1
const MANUAL_REPEAT_MIN = 0
const MANUAL_ITEM_VALUE_MIN = 0
const MANUAL_IDENTIFIER_PATTERN = /^[A-Za-z0-9_-]*$/
const MANUAL_IDENTIFIER_SANITIZE_PATTERN = /[^A-Za-z0-9_-]/g
const MANUAL_SAMPLE_STATUS_OPTIONS = ['final', 'sample'] as const
const MANUAL_TIME_OPTIONS = [1, 2, 3, 4, 5]
const ERROR_CODE_MESSAGE_KEY_MAP: Record<string, string> = {
  DUPLICATE: 'messages.duplicateDataExists',
  TIME_REQUIRED: 'specification.validation.timeRequired',
  SIZE_REQUIRED: 'specification.validation.sizeRequired',
  REPEAT_REQUIRED: 'specification.validation.repeatRequired',
  ITEM_REQUIRED: 'specification.validation.itemRequired',
  ITEMS_REQUIRED: 'specification.validation.itemsRequired',
  ITEM_TITLE_REQUIRED: 'specification.validation.itemTitleRequired',
  ITEM_TITLE_DUPLICATE: 'specification.validation.itemTitleDuplicate',
  ITEM_VALUE_INVALID: 'specification.validation.itemValueInvalid',
  BRAND_REQUIRED: 'specification.validation.brandRequired',
  REPEAT_INVALID: 'specification.validation.repeatInvalid',
  PART_OR_SAMPLE_REQUIRED: 'specification.validation.partOrSampleRequired',
  KIND_REQUIRED: 'specification.validation.kindRequired',
  KIND_INVALID: 'specification.validation.kindInvalid',
  INVALID_DESIGNED_AT: 'specification.validation.invalidDesignedAt',
  HEADER_MISSING: 'specification.validation.headerMissing',
  HEADER_INVALID: 'specification.validation.headerInvalid'
}

type ImportErrorDetail = {
  message: string
  rowNumber?: number
  // valueは型が揺れるためunknownで受け、表示時に型チェックする
  value?: unknown
  duplicates?: {
    row: number
    part: string | null
    sample: string | null
    repeat: number
    size: string
    kind: string
  }[]
}

const getErrorMessageFromApi = (errorBody: ApiErrorBody | null, fallback: string) => {
  const errorCode = errorBody?.code || errorBody?.error_code
  if (errorCode && ERROR_CODE_MESSAGE_KEY_MAP[errorCode]) {
    return i18n.t(ERROR_CODE_MESSAGE_KEY_MAP[errorCode])
  }
  if (i18n.language.startsWith('en')) {
    return fallback
  }
  return errorBody?.message || fallback
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null
}

const isString = (value: unknown): value is string => {
  return typeof value === 'string'
}

const isNumber = (value: unknown): value is number => {
  return typeof value === 'number' && Number.isFinite(value)
}

const isMeasurementItemErrorValue = (value: unknown): value is { title?: unknown, rawValue?: unknown } => {
  return isRecord(value) && ('title' in value || 'rawValue' in value)
}

const isStringArray = (value: unknown): value is string[] => {
  return Array.isArray(value) && value.every(isString)
}

const sanitizeManualIdentifier = (value: string): string => {
  return value.replace(MANUAL_IDENTIFIER_SANITIZE_PATTERN, '')
}

const isManualIdentifier = (value: string): boolean => {
  return MANUAL_IDENTIFIER_PATTERN.test(value)
}

// unknownで受けた値はここで型チェックし、表示可能な文字列へ安全に変換する
const getErrorValueMessage = (value: unknown): string => {
  if (value === undefined || value === null) {
    return ''
  }
  if (isStringArray(value)) {
    const hasValue = value.some((item) => item !== '')
    return hasValue ? `${i18n.t('messages.problemValue')} ${value.join(', ')}` : i18n.t('messages.valueEmpty')
  }
  if (isString(value)) {
    return value.trim() !== '' ? `${i18n.t('messages.problemValue')} ${value}` : i18n.t('messages.valueEmpty')
  }
  if (isMeasurementItemErrorValue(value)) {
    const title = isString(value.title) ? value.title.trim() : ''
    const rawValue = isString(value.rawValue) ? value.rawValue.trim() : ''
    const parts = [
      title !== '' ? `${i18n.t('sample.measurementItem')}:${title}` : '',
      rawValue !== '' ? `${i18n.t('common.value')}:${rawValue}` : ''
    ].filter((part) => part !== '')

    if (parts.length > 0) {
      return `${i18n.t('messages.problemValue')} ${parts.join(' ')}`
    }

    return i18n.t('messages.valueEmpty')
  }
  try {
    const json = JSON.stringify(value)
    return json && json !== '{}' ? `${i18n.t('messages.problemValue')} ${json}` : i18n.t('messages.valueEmpty')
  } catch (error) {
    return `${i18n.t('messages.problemValue')} ${String(value)}`
  }
}

// APIエラーレスポンスを安全に読み取り、必要な情報だけ抽出する
const parseApiErrorBody = async (res: Response): Promise<ApiErrorBody | null> => {
  try {
    const data = await res.json()
    if (!isRecord(data)) {
      return null
    }
    const message = isString(data.message) ? data.message : undefined
    const code = isString(data.code) ? data.code : undefined
    const errorCode = isString(data.error_code) ? data.error_code : undefined
    const rowNumber = isNumber(data.rowNumber) ? data.rowNumber : undefined
    const details = isRecord(data.details) ? data.details : undefined
    const rawValue = data.value ?? details?.value
    const value = rawValue !== undefined ? rawValue : undefined
    const duplicates = Array.isArray(data.duplicates)
      ? data.duplicates
        .filter(isRecord)
        .map((item) => ({
          row: isNumber(item.row) ? item.row : 0,
          part: isString(item.part) ? item.part : null,
          sample: isString(item.sample) ? item.sample : null,
          repeat: isNumber(item.repeat) ? item.repeat : 0,
          size: isString(item.size) ? item.size : '',
          kind: isString(item.kind) ? item.kind : ''
        }))
        .filter((item) => item.row > 0)
      : undefined
    return {
      message,
      code,
      error_code: errorCode,
      rowNumber,
      value,
      duplicates
    }
  } catch (error) {
    return null
  }
}

export function Specification() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const sampleIdFromSearch = getSampleIdFromSearch(location.search)
  const { sample, rowSample } = useContext(SamplesContext)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [specList, setSpecList] = useState<SpecType[]>([])
  const specMetaRef = useRef<SpecMeta>(DEFAULT_SPEC_META)
  const [specMeta, setSpecMeta] = useState<SpecMeta>(DEFAULT_SPEC_META)
  const [isSpecLoading, setIsSpecLoading] = useState(false)
  const [isSpecLoaded, setIsSpecLoaded] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [time, setTime] = useState('')
  const [isModal, setIsModal] = useState(false)
  const [isErrorModal, setIsErrorModal] = useState(false)
  const [isManualInputModalOpen, setIsManualInputModalOpen] = useState(false)
  // const [uploadError, setUploadError] = useState<{ row: number[], message: string }>()
  const [importErrorDetail, setImportErrorDetail] = useState<ImportErrorDetail | null>(null)
  const onCloseErrorModal = useCallback(() => {
    setIsErrorModal(false)
    setImportErrorDetail(null)
  }, [])
  const onCloseManualInputModal = useCallback(() => {
    setIsManualInputModalOpen(false)
  }, [])

  useEffect(() => {
    specMetaRef.current = specMeta
  }, [specMeta])

  useEffect(() => {
    setFile(null)
    setTime('')
  }, [])

  const fetchSpecifications = useCallback(async (options?: { append?: boolean }) => {
    const shouldAppend = options?.append ?? false
    const meta = specMetaRef.current
    const targetPage = shouldAppend ? (meta.nextPage ?? meta.page + 1) : 1
    if (shouldAppend && !meta.hasMore) {
      return
    }
    try {
      setIsSpecLoading(true)
      const params = new URLSearchParams({
        page: String(targetPage),
        limit: String(SPEC_PAGE_LIMIT)
      })
      const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/client/sewing-spec?${params.toString()}`, {
        method: "GET",
        headers: {
          'Content-Type': 'application/json',
        },
        handleUnauthorized: true
      })
      if (res.status === 401) {
        return
      }
      if (res.status === 403) {
        navigate(PATH_INFO.LOGIN.PATH)
        return
      }

      const data = await res.json() as SpecApiResponse
      const items: SpecType[] = Array.isArray(data?.data) ? data.data : []
      setSpecList(prev => {
        if (!shouldAppend) {
          return items
        }
        const map = new Map<string, SpecType>()
        prev.forEach(item => map.set(item.id, item))
        items.forEach((item: SpecType) => map.set(item.id, item))
        return Array.from(map.values())
      })
      setSpecMeta(data.meta ?? DEFAULT_SPEC_META)
    } catch (error) {
      console.log('ERROR FETCH')
    } finally {
      setIsSpecLoading(false)
      setIsSpecLoaded(true)
    }
  }, [navigate])

  const loadMoreSpecifications = useCallback(async () => {
    if (isSpecLoading || !specMetaRef.current.hasMore) {
      return
    }
    await fetchSpecifications({ append: true })
  }, [fetchSpecifications, isSpecLoading])

  useEffect(() => {
    fetchSpecifications()
  }, [fetchSpecifications])

  const resetFileInput = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    setFile(null)
    setTime('')
  }, [])

  const handleFileUpload = useCallback(async (onDuplicateMode?: 'skip' | 'overwrite') => {
    if (!file) return

    const formData = new FormData()
    formData.append('file', file)
    formData.append('time', time)
    if (onDuplicateMode) {
      formData.append('onDuplicate', onDuplicateMode)
    }

    try {
      const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/client/sewing-spec/import/`, {
        method: "POST",
        body: formData,
        handleUnauthorized: true
      })

      if (res.status === 409) {
        const errorBody = await parseApiErrorBody(res)
        setImportErrorDetail({
          message: getErrorMessageFromApi(errorBody, t('common.error')),
          rowNumber: errorBody?.rowNumber,
          value: errorBody?.value,
          duplicates: errorBody?.duplicates
        })
        setIsModal(true)
        return
      }

      if (!res.ok) {
        const errorBody = await parseApiErrorBody(res)
        setImportErrorDetail({
          message: getErrorMessageFromApi(errorBody, t('common.error')),
          rowNumber: errorBody?.rowNumber,
          value: errorBody?.value,
          duplicates: errorBody?.duplicates
        })
        setIsErrorModal(true)
        return
      }

      await fetchSpecifications()
      resetFileInput()
    } catch (error) {
      console.error('Error uploading file')
      resetFileInput()
    }
  }, [file, time, fetchSpecifications, resetFileInput, t])

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const dropped = e.dataTransfer.files[0]
      setFile(dropped)
    }
  }, [])

  return (
    <div className="relative bg-gray-100 rounded-[10px] pt-[22px] pb-[30px] px-[25px] mt-[25px] mx-[40px] min-w-[575px]">
      <div className='flex items-center w-full'>
        <div className='w-2 h-[35px] rounded-sm bg-[#3E3E3E] mr-[5px]' ></div>
        <span className='text-[18px]'>
          {t('pages.specificationImport')}
        </span>
      </div>
      <div className="flex justify-center flex-col-reverse pt-[25px] desktop-md:flex-row desktop-md:space-y-0">
        <Tabele
          specList={specList}
          hasMore={specMeta.hasMore}
          onLoadMore={loadMoreSpecifications}
          isLoaded={isSpecLoaded}
        />
        <div className="flex flex-col min-w-0 max-w-full justify-between mb-2.5 desktop-md:max-w-[1016px] desktop-md:pl-[20px] desktop-md:mb-0">
          <div
            className="flex items-center justify-center w-full h-[372px] bg-[#F9FAFF] rounded-[23px] desktop-md:h-[630px]"
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
          >
            <div className="flex items-center justify-center w-[192px] h-[192px] bg-[#E1E2FF] rounded-[50%]">
              <img src={fileImg} alt="file" className="w-[115px]" />
            </div>
            <div className="relative flex flex-col items-center ml-[26px]">
              <span className="mb-[9px] text-[#545899] font-bold">{t('specification.dropFile')}</span>
              <span className="mb-[12px] text-[#545899] font-bold">{t('specification.or')}</span>
              <div
                className='flex items-center w-[165px] h-[41px] border-[2px] border-[#545899] rounded-[8px] text-[#545899] font-bold bg-white'
              >
                <input
                  type="file"
                  id='file-input'
                  accept='.csv'
                  ref={fileInputRef}
                  className='sr-only'
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setFile(e.target.files[0])
                    }
                    if (fileInputRef.current) {
                      fileInputRef.current.value = ''
                    }
                  }}
                />
                <label htmlFor='file-input' className='flex items-center justify-center cursor-pointer w-full h-full'>
                  <span>
                    {t('specification.selectFile')}
                  </span>
                </label>
              </div>
              <span className="absolute -bottom-[30px] flex max-w-[165px] min-w-0 items-center space-x-[5px] pt-[5px]">
                <p
                  className="flex-1 truncate"
                  title={file?.name || ''}
                  aria-label={file?.name || ''}
                >
                  {file?.name}
                </p>
                {file && <button onClick={() => setFile(null)}>✕</button>}
              </span>
            </div>
          </div>
          <div className="flex justify-center items-center w-full space-x-[14px] mt-[15px] desktop-md:mt-0">
            <button onClick={() => navigate(buildMeasurementPath(sampleIdFromSearch || rowSample.id || sample.id))} className="bg-black w-[158px] h-[37px] rounded-[50px] tablet-lg:w-[200px] tablet-lg:h-[47px]">
              <span className="text-white text-sm tablet-lg:text-base">{t('actions.backToMeasurement')}</span>
            </button>
            <button
              className="min-w-28 max-w-[143px] h-[37px] rounded-[50px] bg-[#C5C7FF] tablet-lg:w-[200px] tablet-lg:h-[47px]"
              onClick={() => setIsManualInputModalOpen(true)}
            >
              <span className="text-[#545899] font-bold text-sm tablet-lg:text-base">{t('common.input')}</span>
            </button>
            <button
              className="w-[143px] h-[37px] rounded-[50px] bg-[#C5C7FF] tablet-lg:w-[200px] tablet-lg:h-[47px]"
              onClick={() => handleFileUpload()}
            >
              <span className="text-[#545899] font-bold text-sm tablet-lg:text-base">{t('common.import')}</span>
            </button>
            <label className="space-x-[5px] h-max">
              <span className="text-[#545899] font-bold">{t('sample.sampleStatus')} :</span>
              <select
                className="h-[30px] bg-transparent text-center pr-[5px] text-[#474980] border-0 border-b-2 border-[#545899] focus:outline-none focus:ring-0 focus:border-[#3a3c69] peer"
                value={time || ''}
                onChange={(e) => { setTime(e.target.value) }}
              >
                <option value={''} hidden>--</option>
                {MANUAL_TIME_OPTIONS.map((value) => (
                  <option key={value} value={String(value)}>{formatOrdinal(value)}</option>
                ))}
              </select>
            </label>
          </div>
        </div>
      </div>
      <DuplicatesModal isOpen={isModal} onClose={() => setIsModal(false)} detail={importErrorDetail} handleFileUpload={handleFileUpload} />
      <ErrorModal isOpen={isErrorModal} onClose={onCloseErrorModal} detail={importErrorDetail} />
      <ManualInputModal
        isOpen={isManualInputModalOpen}
        onClose={onCloseManualInputModal}
        onCreated={fetchSpecifications}
      />
    </div>
  )
}

function Tabele({ specList, hasMore, onLoadMore, isLoaded }: { specList: SpecType[], hasMore: boolean, onLoadMore: () => void, isLoaded: boolean }) {
  const { t, i18n } = useTranslation()
  const isEnglish = i18n.language.startsWith('en')
  const titleTextClass = isEnglish ? 'text-[11px] leading-[1.15] whitespace-nowrap' : 'text-[16px]'
  const sortGroupClass = isEnglish ? 'flex space-x-[8px]' : 'flex space-x-[8px]'
  const sortLabelClass = isEnglish ? 'flex items-center text-[11px] leading-[1.15] whitespace-nowrap' : 'flex items-center'
  const sortRadioClass = isEnglish ? 'w-[22px] h-[22px] mr-[5px] accent-[#545899]' : 'w-[26px] h-[26px] mr-[8px] accent-[#545899]'
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc')
  const [sortedSpecList, setSortedSpecList] = useState<SpecType[]>([])
  const endMessage = isLoaded && !hasMore
    ? <div className="py-3 text-center text-xs text-[#666666]">{t('messages.allDisplayableHistoryLoaded')}</div>
    : (
      <div className="flex items-center justify-center py-3 text-center text-xs text-[#666666]">
        <ReactLoading type="spin" color="#666666" height={16} width={16} className="inline-block mr-2" />
        <span>{t('messages.loadingSpecificationHistory')}</span>
      </div>
    )

  useEffect(() => {
    if (Array.isArray(specList)) {
      const sorted = specList.slice().sort((a, b) => {
        const dateA = new Date(a.created_at).getTime()
        const dateB = new Date(b.created_at).getTime()
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA
      })
      setSortedSpecList(sorted)
    }
  }, [specList, sortOrder])

  return (
    <div
      className="desktop-md:bg-white desktop-md:rounded-[20px] desktop-md:pt-5 desktop-md:pb-[30px] desktop-md:px-4 desktop-md:shadow-[0px_4px_4px_0px_#00000040]"
    >
      <div className='flex items-center justify-between w-full pb-[10px] pl-[9px] pr-[14px]'>
        <span className={`${titleTextClass} opacity-0 desktop-md:opacity-100`}>
          {t('pages.specificationImportHistory')}
        </span>
        <div className={sortGroupClass}>
          <label className={sortLabelClass}>
            <input
              type="radio"
              name="sort"
              className={sortRadioClass}
              checked={sortOrder === 'asc'}
              value="asc"
              onChange={() => setSortOrder('asc')}
            />
            <span>{t('actions.measurementDateAsc')}</span>
          </label>
          <label className={sortLabelClass}>
            <input
              type="radio"
              name="sort"
              className={sortRadioClass}
              checked={sortOrder === 'desc'}
              value="desc"
              onChange={() => setSortOrder('desc')}
            />
            <span>{t('actions.measurementDateDesc')}</span>
          </label>
        </div>
      </div>
      <div className="h-[380px] overflow-y-scroll border border-gray-400 bg-white desktop-md:w-[655px] desktop-md:h-[660px]" id={SPEC_TABLE_SCROLL_ID}>
        <InfiniteScroll
          dataLength={sortedSpecList.length}
          next={onLoadMore}
          hasMore={hasMore}
          loader={
            <div className="flex items-center justify-center py-3 text-center text-xs text-[#666666]">
              <ReactLoading type="spin" color="#666666" height={16} width={16} className="inline-block mr-2" />
              <span>{t('messages.loadingSpecificationHistory')}</span>
            </div>}
          endMessage={endMessage}
          scrollableTarget={SPEC_TABLE_SCROLL_ID}
          scrollThreshold="200px"
          style={{ overflow: "visible" }}
        >
          <table className="w-full">
            <thead className="bg-[#3E3E3E] h-[23px]">
              <tr className="sticky inset-0 bg-[#3E3E3E] text-[12px] text-white">
                <th className="sticky top-0 w-[165px] font-normal">{t('sample.measurementDate')}</th>
                <th className="sticky top-0 w-[115px] font-normal before:content-[''] before:absolute before:inset-0 before:border-l before:border-white">
                  {t('sample.partNumber')}
                </th>
                <th className="sticky top-0 w-[115px] font-normal before:content-[''] before:absolute before:inset-0 before:border-l before:border-white">
                  {t('sample.sampleNumber')}
                </th>
                <th className="sticky top-0 w-[115px] font-normal before:content-[''] before:absolute before:inset-0 before:border-l before:border-white">
                  {t('sample.sampleStatus')}
                </th>
                <th className="sticky top-0 w-[80px] font-normal before:content-[''] before:absolute before:inset-0 before:border-l before:border-white">
                  {t('sample.repeat')}
                </th>
                <th className="sticky top-0 w-[80px] font-normal before:content-[''] before:absolute before:inset-0 before:border-l before:border-white">
                  {t('sample.size')}
                </th>
                <th className="sticky top-0 w-[115px] font-normal before:content-[''] before:absolute before:inset-0 before:border-l before:border-white">
                  {t('sample.color')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {sortedSpecList.map((el: SpecType) => (
                <tr key={el.id} className='text-[14px] h-[30px]'>
                  <td className="h-[30px] border border-l-0 border-t-0 border-gray-400 text-center text-[16px]">{formatDate(el.created_at)}</td>
                  <td className="border border-t-0 border-gray-400 text-center">{el.part_number || ''}</td>
                  <td className="border border-t-0 border-gray-400 text-center">{el.sample_number || ''}</td>
                  <td className="border border-t-0 border-gray-400 text-center">{el.kind === 'final' ? 'FINAL' : formatOrdinal(el.time)}</td>
                  <td className="border border-t-0 border-gray-400 text-center">{el.repeat > 0 ? el.repeat : 0}</td>
                  <td className="border border-t-0 border-gray-400 text-center">{el.size || ''}</td>
                  <td className="border border-t-0 border-gray-400 text-center">{el.color_no || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </InfiniteScroll>
      </div>
    </div>
  )
}

function ManualInputModal({ isOpen, onClose, onCreated }: { isOpen: boolean, onClose: () => void, onCreated: () => Promise<void> }) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [errorMessage, setErrorMessage] = useState('')
  const [duplicateMessage, setDuplicateMessage] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const errorMessageRef = useRef<HTMLParagraphElement | null>(null)
  const [duplicatePayload, setDuplicatePayload] = useState<ManualSpecPayload | null>(null)
  const manualSpecItemIdCounterRef = useRef(MANUAL_SPEC_ITEM_ID_START)
  const createManualSpecItemId = useCallback(() => {
    manualSpecItemIdCounterRef.current += MANUAL_SPEC_ITEM_ID_STEP
    return `manual-item-${manualSpecItemIdCounterRef.current}`
  }, [])
  const createManualSpecItem = useCallback((): ManualSpecItemInput => ({
    id: createManualSpecItemId(),
    title: '',
    value: ''
  }), [createManualSpecItemId])
  const getTodayDateInputValue = useCallback(() => {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }, [])
  const createManualSpecForm = useCallback((): ManualSpecForm => ({
    brand: '',
    partNumber: '',
    sampleNumber: '',
    repeat: '',
    itemName: '',
    manufacturer: '',
    patternNumber: '',
    kind: '',
    designedAt: getTodayDateInputValue(),
    time: '',
    colorNo: '',
    size: '',
    items: [createManualSpecItem()]
  }), [createManualSpecItem, getTodayDateInputValue])
  const [form, setForm] = useState<ManualSpecForm>(() => createManualSpecForm())

  useEffect(() => {
    if (isOpen) {
      manualSpecItemIdCounterRef.current = MANUAL_SPEC_ITEM_ID_START
      setForm(createManualSpecForm())
      setErrorMessage('')
      setDuplicateMessage('')
      setDuplicatePayload(null)
      setIsSaving(false)
    }
  }, [createManualSpecForm, isOpen])

  useEffect(() => {
    if (!errorMessage) return
    errorMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [errorMessage])

  useEffect(() => {
    setErrorMessage('')
    setDuplicateMessage('')
  }, [i18n.language])

  const updateForm = useCallback((patch: Partial<ManualSpecForm>) => {
    setForm((prev) => ({ ...prev, ...patch }))
  }, [])

  const updateItem = useCallback((index: number, patch: Partial<ManualSpecItemInput>) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((item, idx) => (idx === index ? { ...item, ...patch } : item))
    }))
  }, [])

  const addItem = useCallback(() => {
    setForm((prev) => ({
      ...prev,
      items: [...prev.items, createManualSpecItem()]
    }))
  }, [createManualSpecItem])

  const removeItem = useCallback((index: number) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.filter((_, idx) => idx !== index)
    }))
  }, [])

  const trimText = useCallback((value: string) => value.trim(), [])

  const buildPayload = useCallback((): BuildPayloadResult => {
    const brand = trimText(form.brand)
    const partNumber = trimText(form.partNumber)
    const sampleNumber = trimText(form.sampleNumber)
    const itemName = trimText(form.itemName)
    const manufacturer = trimText(form.manufacturer)
    const patternNumber = trimText(form.patternNumber)
    const colorNo = trimText(form.colorNo)
    const size = trimText(form.size)
    const repeatValue = Number(form.repeat)

    if (!brand) return { ok: false, error: t('specification.validation.brandRequired') }
    if (!itemName) return { ok: false, error: t('specification.validation.itemRequired') }
    if (!partNumber && !sampleNumber) return { ok: false, error: t('specification.validation.partOrSampleRequired') }
    if (partNumber && !isManualIdentifier(partNumber)) return { ok: false, error: t('specification.validation.partNumberInvalid') }
    if (sampleNumber && !isManualIdentifier(sampleNumber)) return { ok: false, error: t('specification.validation.sampleNumberInvalid') }
    if (patternNumber && !isManualIdentifier(patternNumber)) return { ok: false, error: t('specification.validation.patternNumberInvalid') }
    if (!form.repeat) return { ok: false, error: t('specification.validation.repeatRequired') }
    if (!Number.isFinite(repeatValue)) return { ok: false, error: t('specification.validation.repeatInvalid') }
    if (repeatValue < MANUAL_REPEAT_MIN) return { ok: false, error: t('specification.validation.repeatMin') }
    if (!form.kind) return { ok: false, error: t('specification.validation.kindRequired') }
    if (!form.designedAt) return { ok: false, error: t('specification.validation.designedAtRequired') }
    if (form.kind === 'sample' && !form.time) return { ok: false, error: t('specification.validation.timeRequired') }
    if (!size) return { ok: false, error: t('specification.validation.sizeRequired') }

    const normalizedItems = form.items
      .map((item) => ({ title: trimText(item.title), value: trimText(item.value) }))
      .filter((item) => item.title !== '' || item.value !== '')

    if (normalizedItems.length === 0) return { ok: false, error: t('specification.validation.itemsRequired') }

    for (let i = 0; i < normalizedItems.length; i += 1) {
      const item = normalizedItems[i]
      if (!item.title) return { ok: false, error: t('specification.validation.itemTitleRequired') }
      if (item.value === '') return { ok: false, error: t('specification.validation.itemValueRequired') }
      if (!Number.isFinite(Number(item.value))) return { ok: false, error: t('specification.validation.itemValueInvalid') }
      if (Number(item.value) < MANUAL_ITEM_VALUE_MIN) return { ok: false, error: t('specification.validation.itemValueMin') }
    }

    return {
      ok: true,
      payload: {
        brand,
        part_number: partNumber || null,
        sample_number: sampleNumber || null,
        repeat: repeatValue,
        item_name: itemName,
        manufacturer: manufacturer || null,
        pattern_number: patternNumber || null,
        kind: form.kind as 'final' | 'sample',
        designed_at: form.designedAt,
        time: form.kind === 'sample' ? Number(form.time) : null,
        color_no: colorNo || null,
        size,
        items: normalizedItems.map((item) => ({
          title: item.title,
          value: Number(item.value)
        }))
      }
    }
  }, [form, trimText, t])

  const submitManualSpec = useCallback(async (payload: ManualSpecPayload) => {
    setIsSaving(true)
    setErrorMessage('')
    setDuplicateMessage('')
    try {
      const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/client/sewing-spec`, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        handleUnauthorized: true
      })

      if (res.status === 401) {
        return
      }
      if (res.status === 403) {
        navigate(PATH_INFO.LOGIN.PATH)
        return
      }

      if (res.status === 409) {
        const errorBody = await parseApiErrorBody(res)
        setDuplicateMessage(getErrorMessageFromApi(errorBody, t('messages.duplicateDataExists')))
        setDuplicatePayload(payload)
        return
      }

      if (!res.ok) {
        const errorBody = await parseApiErrorBody(res)
        setErrorMessage(getErrorMessageFromApi(errorBody, t('messages.genericRegistrationFailed')))
        return
      }

      await onCreated()
      onClose()
    } catch (error) {
      setErrorMessage(t('messages.genericRegistrationFailed'))
    } finally {
      setIsSaving(false)
    }
  }, [navigate, onClose, onCreated, t])

  const handleSubmit = useCallback(async () => {
    const result = buildPayload()
    if (!result.ok) {
      setErrorMessage(result.error)
      return
    }
    setDuplicatePayload(null)
    await submitManualSpec(result.payload)
  }, [buildPayload, submitManualSpec])

  const handleOverwrite = useCallback(async () => {
    if (!duplicatePayload) return
    await submitManualSpec({ ...duplicatePayload, onDuplicate: 'overwrite' })
  }, [duplicatePayload, submitManualSpec])

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        className="min-w-[450px] max-w-[1000px] min-h-[250px] self-start my-6 flex max-h-[calc(100vh-48px)] flex-col overflow-hidden p-[25px] desktop-md:my-0 desktop-md:self-auto desktop-md:max-h-[90vh]"
      >
        <div className="flex min-h-0 w-full flex-col">
          <div className="flex items-center mb-4">
            <div className='w-2 h-[35px] rounded-sm bg-[#3E3E3E] mr-[5px]' ></div>
            <span className="text-[18px] font-bold">{t('pages.specificationRegister')}</span>
          </div>
          <div className="min-h-0 overflow-y-auto pr-1">
            <div className="grid grid-cols-1 items-start gap-4 desktop-md:grid-cols-[minmax(320px,1fr)_minmax(420px,1.35fr)]">
              <div className="rounded border border-[#D1D5DB] bg-[#FAFAFA] p-3">
                <div className="space-y-3">
                  <label className="grid grid-cols-[130px_minmax(0,1fr)] items-center gap-2 text-sm text-[#333] desktop-md:grid-cols-[160px_minmax(0,1fr)]">
                    <span>{t('specification.dateOrCreatedAt')}<span className="text-red-600"> *</span></span>
                    <input
                      type="date"
                      className="w-full rounded border border-[#D1D5DB] bg-white px-2 py-1 shadow-[inset_0_1px_2px_rgba(15,23,42,0.08)]"
                      value={form.designedAt}
                      onChange={(e) => updateForm({ designedAt: e.target.value })}
                    />
                  </label>
                  <label className="grid grid-cols-[130px_minmax(0,1fr)] items-center gap-2 text-sm text-[#333] desktop-md:grid-cols-[160px_minmax(0,1fr)]">
                    <span>{t('specification.brandOrCustomerName')}<span className="text-red-600"> *</span></span>
                    <input
                      type="text"
                      className="w-full rounded border border-[#D1D5DB] bg-white px-2 py-1 shadow-[inset_0_1px_2px_rgba(15,23,42,0.08)]"
                      value={form.brand}
                      onChange={(e) => updateForm({ brand: e.target.value })}
                      placeholder={t('placeholders.exampleBrandName')}
                    />
                  </label>
                  <label className="grid grid-cols-[130px_minmax(0,1fr)] items-center gap-2 text-sm text-[#333] desktop-md:grid-cols-[160px_minmax(0,1fr)]">
                    <span>{t('sample.item')}<span className="text-red-600"> *</span></span>
                    <input
                      type="text"
                      className="w-full rounded border border-[#D1D5DB] bg-white px-2 py-1 shadow-[inset_0_1px_2px_rgba(15,23,42,0.08)]"
                      value={form.itemName}
                      onChange={(e) => updateForm({ itemName: e.target.value })}
                      placeholder={t('placeholders.exampleTshirt')}
                    />
                  </label>
                  <label className="grid grid-cols-[130px_minmax(0,1fr)] items-center gap-2 text-sm text-[#333] desktop-md:grid-cols-[160px_minmax(0,1fr)]">
                    <span>{t('sample.partNumber')}</span>
                    <input
                      type="text"
                      className="w-full rounded border border-[#D1D5DB] bg-white px-2 py-1 shadow-[inset_0_1px_2px_rgba(15,23,42,0.08)]"
                      value={form.partNumber}
                      onChange={(e) => updateForm({ partNumber: sanitizeManualIdentifier(e.target.value) })}
                      placeholder={t('placeholders.examplePartNumber')}
                    />
                  </label>
                  <label className="grid grid-cols-[130px_minmax(0,1fr)] items-center gap-2 text-sm text-[#333] desktop-md:grid-cols-[160px_minmax(0,1fr)]">
                    <span>{t('sample.sampleNumber')}</span>
                    <input
                      type="text"
                      className="w-full rounded border border-[#D1D5DB] bg-white px-2 py-1 shadow-[inset_0_1px_2px_rgba(15,23,42,0.08)]"
                      value={form.sampleNumber}
                      onChange={(e) => updateForm({ sampleNumber: sanitizeManualIdentifier(e.target.value) })}
                      placeholder={t('placeholders.exampleSampleNumber')}
                    />
                  </label>
                  <label className="grid grid-cols-[130px_minmax(0,1fr)] items-center gap-2 text-sm text-[#333] desktop-md:grid-cols-[160px_minmax(0,1fr)]">
                    <span>{t('sample.repeat')}<span className="text-red-600"> *</span></span>
                    <input
                      type="number"
                      min={MANUAL_REPEAT_MIN}
                      className="w-full rounded border border-[#D1D5DB] bg-white px-2 py-1 shadow-[inset_0_1px_2px_rgba(15,23,42,0.08)]"
                      value={form.repeat}
                      onChange={(e) => {
                        const nextRepeat = e.target.value
                        if (nextRepeat === '') {
                          updateForm({ repeat: '' })
                          return
                        }
                        const parsedRepeat = Number(nextRepeat)
                        if (!Number.isFinite(parsedRepeat)) {
                          return
                        }
                        if (parsedRepeat < MANUAL_REPEAT_MIN) {
                          updateForm({ repeat: String(MANUAL_REPEAT_MIN) })
                          return
                        }
                        updateForm({ repeat: nextRepeat })
                      }}
                      placeholder={t('placeholders.exampleRepeat')}
                    />
                  </label>
                  <label className="grid grid-cols-[130px_minmax(0,1fr)] items-center gap-2 text-sm text-[#333] desktop-md:grid-cols-[160px_minmax(0,1fr)]">
                    <span>{t('specification.productionOrSample')}<span className="text-red-600"> *</span></span>
                    <select
                      className="w-full rounded border border-[#D1D5DB] bg-white px-2 py-1 shadow-[inset_0_1px_2px_rgba(15,23,42,0.08)]"
                      value={form.kind}
                      onChange={(e) => updateForm({ kind: e.target.value as ManualSpecForm['kind'] })}
                    >
                      <option value="" hidden>--</option>
                      {MANUAL_SAMPLE_STATUS_OPTIONS.map((value) => (
                        <option key={value} value={value}>{value === 'final' ? t('sample.final') : t('sample.sample')}</option>
                      ))}
                    </select>
                  </label>
                  <label className="grid grid-cols-[130px_minmax(0,1fr)] items-center gap-2 text-sm text-[#333] desktop-md:grid-cols-[160px_minmax(0,1fr)]">
                    <span>{t('sample.sampleStatus')}{form.kind === 'sample' && <span className="text-red-600"> *</span>}</span>
                    <select
                      className={`w-full rounded border border-[#D1D5DB] px-2 py-1 shadow-[inset_0_1px_2px_rgba(15,23,42,0.08)] ${form.kind !== 'sample' ? 'bg-[#F3F4F6] cursor-not-allowed' : 'bg-white'}`}
                      value={form.time}
                      onChange={(e) => updateForm({ time: e.target.value })}
                      disabled={form.kind !== 'sample'}
                    >
                      <option value="" hidden>--</option>
                      {MANUAL_TIME_OPTIONS.map((value) => (
                        <option key={value} value={String(value)}>{formatOrdinal(value)}</option>
                      ))}
                    </select>
                  </label>
                  <label className="grid grid-cols-[130px_minmax(0,1fr)] items-center gap-2 text-sm text-[#333] desktop-md:grid-cols-[160px_minmax(0,1fr)]">
                    <span>{t('sample.size')}<span className="text-red-600"> *</span></span>
                    <SizeCandidateInput
                      value={form.size}
                      onChange={(value) => updateForm({ size: value })}
                      placeholder={t('placeholders.sizeInput')}
                    />
                  </label>
                  <label className="grid grid-cols-[130px_minmax(0,1fr)] items-center gap-2 text-sm text-[#333] desktop-md:grid-cols-[160px_minmax(0,1fr)]">
                    <span>{t('sample.color')}</span>
                    <input
                      type="text"
                      className="w-full rounded border border-[#D1D5DB] bg-white px-2 py-1 shadow-[inset_0_1px_2px_rgba(15,23,42,0.08)]"
                      value={form.colorNo}
                      onChange={(e) => updateForm({ colorNo: e.target.value })}
                      placeholder={t('placeholders.exampleWhite')}
                    />
                  </label>
                  <label className="grid grid-cols-[130px_minmax(0,1fr)] items-center gap-2 text-sm text-[#333] desktop-md:grid-cols-[160px_minmax(0,1fr)]">
                    <span>{t('specification.factoryName')}</span>
                    <input
                      type="text"
                      className="w-full rounded border border-[#D1D5DB] bg-white px-2 py-1 shadow-[inset_0_1px_2px_rgba(15,23,42,0.08)]"
                      value={form.manufacturer}
                      onChange={(e) => updateForm({ manufacturer: e.target.value })}
                      placeholder={t('placeholders.exampleFactory')}
                    />
                  </label>
                  <label className="grid grid-cols-[130px_minmax(0,1fr)] items-center gap-2 text-sm text-[#333] desktop-md:grid-cols-[160px_minmax(0,1fr)]">
                    <span>{t('specification.patternNumber')}</span>
                    <input
                      type="text"
                      className="w-full rounded border border-[#D1D5DB] bg-white px-2 py-1 shadow-[inset_0_1px_2px_rgba(15,23,42,0.08)]"
                      value={form.patternNumber}
                      onChange={(e) => updateForm({ patternNumber: sanitizeManualIdentifier(e.target.value) })}
                      placeholder={t('placeholders.examplePatternNumber')}
                    />
                  </label>
                </div>
              </div>
              <div className="rounded border border-[#D1D5DB] bg-white p-3">
                <div className="flex items-center justify-between">
                  <span className="inline-flex flex-wrap items-baseline gap-1 text-sm font-bold">
                    <span>{t('sample.measurementItem')}</span>
                    <span className="text-xs font-medium text-[#6B7280]">({t('placeholders.exampleMeasurementItem')})</span>
                    <span className="text-red-600">*</span>
                  </span>
                  <button
                    type="button"
                    className="border px-3 py-1 rounded-[20px] bg-[#C5C7FF] text-[#545899] text-xs font-semibold"
                    onClick={addItem}
                  >
                    + {t('actions.add')}
                  </button>
                </div>
                <div className="mt-2 max-h-[480px] overflow-y-auto rounded border border-[#D1D5DB]">
                  <table className="w-full table-fixed border-collapse">
                    <thead className="sticky top-0 z-10 bg-[#F6F7FB]">
                      <tr className="text-left text-xs bg-[#3E3E3E] text-white">
                        <th className="border-b border-r border-[#D1D5DB] px-2 py-2 font-medium">{t('specification.measurementItemName')}</th>
                        <th className="w-[120px] border-b border-r border-[#D1D5DB] px-2 py-2 font-medium">{t('specification.measurementValue')}</th>
                        <th className="w-[80px] border-b border-[#D1D5DB] px-2 py-2 font-medium text-center">{t('specification.operation')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.items.map((item, index) => (
                        <tr key={item.id} className="text-sm">
                          <td className="border-b border-r border-[#D1D5DB] p-2 align-top">
                            {/* 支援技術向けに、sr-onlyでlabel付与 */}
                            <label htmlFor={`measure-item-title-${item.id}`} className="sr-only">
                              {t('specification.measurementItemName')}
                            </label>
                            <input
                              id={`measure-item-title-${item.id}`}
                              type="text"
                              className="w-full rounded border border-[#D1D5DB] px-2 py-1 shadow-[inset_0_1px_2px_rgba(15,23,42,0.08)]"
                              placeholder={t('placeholders.measurementItemInput')}
                              value={item.title}
                              onChange={(e) => updateItem(index, { title: e.target.value })}
                            />
                          </td>
                          <td className="border-b border-r border-[#D1D5DB] p-2 align-top">
                            {/* 支援技術向けに、sr-only で label を付与 */}
                            <label htmlFor={`measure-item-value-${item.id}`} className="sr-only">
                              {t('specification.measurementValue')}
                            </label>
                            <input
                              id={`measure-item-value-${item.id}`}
                              type="number"
                              min={MANUAL_ITEM_VALUE_MIN}
                              step="0.1"
                              className="w-full rounded border border-[#D1D5DB] px-2 py-1 shadow-[inset_0_1px_2px_rgba(15,23,42,0.08)]"
                              placeholder={t('placeholders.exampleMeasurementValue')}
                              value={item.value}
                              onChange={(e) => {
                                const nextValue = e.target.value
                                if (nextValue === '') {
                                  updateItem(index, { value: '' })
                                  return
                                }
                                const parsedValue = Number(nextValue)
                                if (!Number.isFinite(parsedValue)) {
                                  return
                                }
                                if (parsedValue < MANUAL_ITEM_VALUE_MIN) {
                                  updateItem(index, { value: String(MANUAL_ITEM_VALUE_MIN) })
                                  return
                                }
                                updateItem(index, { value: nextValue })
                              }}
                            />
                          </td>
                          <td className="border-b border-[#D1D5DB] p-2 text-center align-top">
                            {form.items.length > 1 && (
                              <button
                                type="button"
                                className="border px-2 py-0.5 rounded-[20px] text-xs bg-[#FBD1D8] border-[#F7B8C4] text-[#b85f5f] font-semibold shadow-[0_1px_2px_rgba(155,44,44,0.35)]"
                                onClick={() => removeItem(index)}
                              >
                                {t('common.delete')}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex shrink-0 items-center justify-end space-x-3">
            {errorMessage && (
              <p ref={errorMessageRef} className="mr-auto text-sm text-red-600">
                {errorMessage}
              </p>
            )}
            <button
              className="border px-4 py-2 rounded-[25px] text-[#666] font-bold"
              onClick={onClose}
              disabled={isSaving}
            >
              {t('common.close')}
            </button>
            <button
              className="border px-4 py-2 rounded-[25px] bg-[#C5C7FF] text-[#545899] font-bold"
              onClick={handleSubmit}
              disabled={isSaving}
            >
              {t('common.register')}
            </button>
          </div>
        </div>
      </Modal>
      <ManualDuplicateConfirmModal
        isOpen={duplicateMessage !== ''}
        message={duplicateMessage}
        isSaving={isSaving}
        onCancel={() => {
          setDuplicateMessage('')
          setDuplicatePayload(null)
        }}
        onOverwrite={handleOverwrite}
      />
    </>
  )
}

function DuplicatesModal({ isOpen, onClose, detail, handleFileUpload }: { isOpen: boolean, onClose: () => void, detail: ImportErrorDetail | null, handleFileUpload: (action: 'skip' | 'overwrite') => void }) {
  const { t } = useTranslation()
  const duplicateRows = detail?.duplicates?.map((item) => item.row).filter((row) => typeof row === 'number') ?? []
  const uniqueDuplicateRows = Array.from(new Set(duplicateRows))
  const hasDuplicateRows = uniqueDuplicateRows.length > 0
  const fallbackRowNumber = detail?.rowNumber
  const duplicateRowsText = hasDuplicateRows
    ? uniqueDuplicateRows.map((row) => t('specification.csvRow', { row })).join(t('common.comma'))
    : fallbackRowNumber
      ? t('specification.csvRow', { row: fallbackRowNumber })
      : t('specification.csvRowFallback')
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="min-w-[450px] max-w-[1000px] min-h-[250px] max-h-fit flex flex-col items-center justify-center p-[25px]"
    >
      <span className="text-[16px] text-center">
        <span>{t('messages.csvDuplicateMessage', { rows: duplicateRowsText })}</span>
        <p>{t('messages.overwriteConfirm')}</p>
      </span>
      <div className="space-x-[25px] mt-[35px]">
        <button
          className="border p-2 rounded-[25px] w-[150px] border-[#666]"
          onClick={() => {
            handleFileUpload('skip')
            onClose()
          }}
        >
          <p className="text-[#666] font-bold">{t('common.no')}</p>
        </button>
        <button
          className="border p-2 rounded-[25px] w-[150px] bg-[#C5C7FF]"
          onClick={() => {
            handleFileUpload('overwrite')
            onClose()
          }}>
          <p className="text-[#545899] font-bold">{t('common.yes')}</p>
        </button>
      </div>
    </Modal>
  )
}

function ManualDuplicateConfirmModal({
  isOpen,
  message,
  isSaving,
  onCancel,
  onOverwrite
}: {
  isOpen: boolean
  message: string
  isSaving: boolean
  onCancel: () => void
  onOverwrite: () => void
}) {
  const { t } = useTranslation()

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      className="min-w-[300px] max-w-[300px] min-h-[150px] flex flex-col items-center justify-center p-[25px]"
    >
      <div className="w-full text-center">
        <p className="text-[16px]">{message || t('messages.duplicateDataExists')}</p>
        <p className="mt-2 text-[14px]">{t('messages.overwriteAndSaveConfirm')}</p>
        <div className="mt-[25px] flex items-center justify-center space-x-3">
          <button
            className="border px-4 py-2 rounded-[25px] w-[100px] text-[#666] font-bold"
            onClick={onCancel}
            disabled={isSaving}
          >
            {t('common.no')}
          </button>
          <button
            className="border px-4 py-2 rounded-[25px] w-[100px] bg-[#C5C7FF] text-[#545899] font-bold"
            onClick={onOverwrite}
            disabled={isSaving}
          >
            {t('common.yes')}
          </button>
        </div>
      </div>
    </Modal>
  )
}

function ErrorModal({ isOpen, onClose, detail }: { isOpen: boolean, onClose: () => void, detail: ImportErrorDetail | null }) {
  const { t } = useTranslation()
  const errorValueMessage = getErrorValueMessage(detail?.value)
  const hasDetailValue = detail?.value !== undefined && detail?.value !== null
  const rowText = detail?.rowNumber ? t('specification.csvRow', { row: detail.rowNumber }) : ''

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="min-w-[480px] max-w-fit max-h-[460px] flex flex-col items-center justify-center p-6"
    >
      <div className="w-full">
        <div className="flex items-center justify-center">
          <img src={errorImg} alt="error" className="w-6 mr-2" />
          <p className="text-center font-bold text-[16px]">
            {detail?.message || t('common.error')}
          </p>
        </div>
        {hasDetailValue && (
          <p className="mt-3 text-center text-[14px] text-[#555] break-all">
            {errorValueMessage}
          </p>
        )}
        <p className="mt-4 text-center text-[12px]">
          {t('messages.csvFixAndUpload', { rowText })}
        </p>
      </div>
      <button
        onClick={onClose}
        className="border p-2 rounded-[25px] w-[150px] mt-[25px] border-[#666]"
      >
        <p className="text-[#666] font-bold">{t('common.back')}</p>
      </button>
    </Modal>
  )
}
